import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { type LoginInput, type Permission, type SessionDto } from '@musleempay/shared';
import { ApiError } from '../lib/api-client.js';
import { clearSupportGrant } from '../lib/support-access.js';
import { authService } from '../services/index.js';
import { queryKeys } from './queries.js';

/**
 * Authentication context.
 *
 * The session is a normal TanStack query, so every screen shares one cached
 * copy and a sign-out invalidates it everywhere at once.
 */

export interface AuthContextValue {
  readonly session: SessionDto | null;
  readonly isLoading: boolean;
  readonly isAuthenticated: boolean;
  login(input: LoginInput): Promise<SessionDto>;
  logout(): Promise<void>;
  /** Permission check driven by what the API says this user may do. */
  can(permission: Permission): boolean;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const client = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) => authService.session(signal),
    // A 401 here is the normal signed-out state, not a failure worth retrying.
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.isAuthError) return false;
      return failureCount < 2;
    },
    staleTime: 5 * 60_000,
    // 'always', not true: cookies are shared by every tab, so signing in as someone
    // else in one tab changes who the others are. A tab left on /admin must notice
    // on focus, not up to five minutes later, and be moved out by its route guard.
    refetchOnWindowFocus: 'always',
  });

  const session =
    sessionQuery.data ??
    (sessionQuery.error instanceof ApiError && sessionQuery.error.isAuthError ? null : null);

  const login = useCallback(
    async (input: LoginInput): Promise<SessionDto> => {
      // A new sign-in starts clean: a grant left over from a previous operator in
      // this tab must not carry over to whoever signs in next.
      clearSupportGrant();
      const result = await authService.login(input);
      client.setQueryData(queryKeys.session, result);
      return result;
    },
    [client],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await authService.logout();
    } finally {
      // Leaving ends any support view too, whether or not the call succeeded.
      clearSupportGrant();
      // Clear everything: cached data belongs to the account that just left.
      client.clear();
      client.setQueryData(queryKeys.session, null);
    }
  }, [client]);

  const refresh = useCallback(async (): Promise<void> => {
    await client.invalidateQueries({ queryKey: queryKeys.session });
  }, [client]);

  const can = useCallback(
    (permission: Permission): boolean => {
      if (!session) return false;
      // The server enforces this regardless; the client check only decides
      // whether to render a control the user could not use anyway.
      if (session.isPlatformAdmin) return true;
      return session.permissions.includes(permission);
    },
    [session],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isLoading: sessionQuery.isLoading,
      isAuthenticated: session !== null,
      login,
      logout,
      can,
      refresh,
    }),
    [session, sessionQuery.isLoading, login, logout, can, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return context;
}
